import { ButtonProps } from "@mui/material/Button";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import { useCallback, useMemo, useRef, useState } from "react";
import { Dialog } from "../../../components/Dialog";
import { useStyles } from "../styles";
import { Insights } from "../../../components/Enrichment/Insights";
import { Rules } from "../../../components/Enrichment/Rules";
import { Review } from "../../../components/Enrichment/Review";
import { Formik, FormikProps } from "formik";
import * as Yup from "yup";
import { CreateEnrichmentData } from "../../../types/enrichment";
import { useMutation } from "react-query";
import { createEnrichment } from "../../../services/enrichment";
import { useToast } from "../../../hooks/useToast";
import { queryClient } from "../../../services/queryClient";
import { map } from "lodash";
import Stack from "@mui/material/Stack";

type NewEnrichmentProps = {
  open: boolean;
  onClose: () => void;
};

const validationSchema = Yup.object().shape({
  insight: Yup.object()
    .shape({
      idListProspectei: Yup.number().required(),
      nameList: Yup.string().required(),
    })
    .required("Selecione uma audiência"),
  coast: Yup.number().required("Valor inválido"),
  quantity: Yup.number().required("Quantidade inválida"),
  rule: Yup.string().required("Regra inválida"),
});

export function NewEnrichment({ open, onClose }: NewEnrichmentProps) {
  const toast = useToast();
  const styles = useStyles();

  const formikRef = useRef<FormikProps<CreateEnrichmentData>>(null);
  const [activeStep, setActiveStep] = useState<number>(0);

  const newEnrichment = useMutation(
    async (data: CreateEnrichmentData) => {
      const response = await createEnrichment(data);
      return response;
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries("enrichments");
        setActiveStep(0);
        onClose();
        toast.show(response.message, {});
      },
    }
  );

  const handleSave = useCallback(
    async (data: CreateEnrichmentData) => {
      await newEnrichment.mutateAsync(data);
    },
    [newEnrichment]
  );

  const handleClose = useCallback(() => {
    setActiveStep(0);
    onClose();
  }, [onClose, setActiveStep]);

  const renderContent = useCallback(() => {
    switch (activeStep) {
      case 0:
        return <Insights />;
      case 1:
        return <Rules />;
      case 2:
        return <Review />;
      default:
        return <></>;
    }
  }, [activeStep]);

  const steps = useMemo(() => {
    return ["Audiência", "Regras", "Resumo"];
  }, []);

  const actions: ButtonProps[] = useMemo(() => {
    return [
      {
        children: "Voltar",
        onClick: () => setActiveStep((prev) => prev - 1),
        disabled: activeStep === 0 || newEnrichment.isLoading,
      },
      {
        disabled: newEnrichment.isLoading,
        children: newEnrichment.isLoading
          ? "Enviado solicitação..."
          : activeStep === 2
          ? "Enriquecer"
          : "Continuar",
        variant: "contained",
        onClick: () =>
          activeStep === 2
            ? formikRef.current?.handleSubmit()
            : setActiveStep((prev) => prev + 1),
      },
    ];
  }, [activeStep, formikRef, newEnrichment.isLoading]);

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      title="Novo enriquecimento"
      open={open}
      actions={actions}
      onClose={handleClose}
    >
      <Stepper activeStep={activeStep}>
        {map(steps, (step) => {
          return (
            <Step key={step}>
              <StepLabel>{step}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <Stack spacing={2} className={styles.rootContent}>
        <Formik
          innerRef={formikRef}
          validateOnChange={false}
          validationSchema={validationSchema}
          onSubmit={handleSave}
          initialValues={
            {
              rule: "update",
              key: "document",
            } as CreateEnrichmentData
          }
        >
          {renderContent()}
        </Formik>
      </Stack>
    </Dialog>
  );
}
